import { sequences } from '@magenta/music/es6/core';
import { NoteSequence, INoteSequence } from '@magenta/music/es6/protobuf';
import TonalChord from '@tonaljs/chord';
import { ChordProgression } from './types';

export function recalculateSequenceMeta(sequence: INoteSequence): NoteSequence {
  const updatedSequence = new NoteSequence(sequence);
  const isQuantizedSequence = sequences.isQuantizedSequence(updatedSequence);
  if (updatedSequence.notes && updatedSequence.notes.length) {
    const lastNote = updatedSequence.notes[updatedSequence.notes.length - 1];
    if (isQuantizedSequence) {
      updatedSequence.totalQuantizedSteps = lastNote.quantizedEndStep || 0;
    } else {
      updatedSequence.totalTime = lastNote.endTime || 0;
    }
  } else if (isQuantizedSequence) {
    updatedSequence.totalQuantizedSteps = 0;
  } else {
    updatedSequence.totalTime = 0;
  }
  return updatedSequence;
}

export function getSequenceDuration(sequence: INoteSequence, stepsPerSecond = 1): number {
  if (!sequence.notes || !sequence.notes.length) return 0;
  const { notes } = sequence;
  if (sequences.isQuantizedSequence(sequence)) {
    const steps = (
      (notes[notes.length - 1].quantizedEndStep || 0)
      - (notes[0].quantizedStartStep || 0)
    );
    return steps / stepsPerSecond;
  }
  return (
    (notes[notes.length - 1].endTime || 0)
    - (notes[0].startTime || 0)
  );
}

export function getStartOfSequence(
  sequence: INoteSequence,
  seconds: number,
  stepsPerSecond = 1,
): NoteSequence {
  if (!sequence.notes || !sequence.notes.length) return new NoteSequence(sequence);
  const firstNote = sequence.notes[0];
  let startTime = 0;
  let endTime = 0;
  if (sequences.isQuantizedSequence(sequence)) {
    startTime = firstNote.quantizedStartStep || 0;
    endTime = startTime + (seconds * stepsPerSecond);
  } else {
    startTime = firstNote.startTime || 0;
    endTime = startTime + seconds;
  }
  return recalculateSequenceMeta(sequences.trim(sequence, startTime, endTime));
}

export function getEndOfSequence(
  sequence: INoteSequence,
  seconds: number,
  stepsPerSecond = 1,
): NoteSequence {
  if (!sequence.notes || !sequence.notes.length) return new NoteSequence(sequence);
  const lastNote = sequence.notes[sequence.notes.length - 1];
  let startTime = 0;
  let endTime = 0;
  if (sequences.isQuantizedSequence(sequence)) {
    endTime = lastNote.quantizedEndStep || 0;
    startTime = endTime - (seconds * stepsPerSecond);
  } else {
    endTime = lastNote.endTime || 0;
    startTime = endTime - seconds;
  }
  return recalculateSequenceMeta(sequences.trim(sequence, startTime, endTime));
}

/** Flatten a ChordProgression into a list of chord names */
export function flattenChordProgression(progression: ChordProgression): string[] {
  // TonalChord in some cases outputs more than one chord string
  // Only taking the first string produced for now
  // Reference: https://github.com/tonaljs/tonal/tree/master/packages/chord-detect
  // Removing inversions (delimited by '/') due to incompatibility with magenta models
  return progression.chords.map(({ notes }) => TonalChord.detect(notes)[0].split('/')[0]);
}
